/**
 * Shared AI Assist Toolbar — reusable across Content Studio & edit drawers
 * Every button is an immediate action (no toggle-then-apply).
 * Includes undo/redo history.
 */
import { client } from '../api/client.js';

// Decode HTML entities like &#39; &amp; &quot; into raw chars.
// Uses a textarea trick: setting innerHTML then reading .value auto-decodes.
function _decodeEntities(text) {
    if (typeof text !== 'string' || !text.includes('&')) return text;
    const ta = document.createElement('textarea');
    ta.innerHTML = text;
    let out = ta.value;
    // Handle double-encoded entities (&amp;#39; → &#39; → ')
    if (out.includes('&') && /&\w+;|&#\d+;/.test(out)) {
        ta.innerHTML = out;
        out = ta.value;
    }
    return out;
}

// Clean up LLM responses: strip JSON/markdown wrappers + decode entities
function _cleanLlmText(text) {
    if (typeof text !== 'string') return text;
    let out = text.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
    if (out.startsWith('{') && out.includes('"text"')) {
        try { const p = JSON.parse(out); if (p.text) out = p.text; } catch (_) {}
    }
    return _decodeEntities(out);
}

// Per-toolbar undo/redo stacks keyed by a toolbar ID
const _undoStacks = {};
const _redoStacks = {};

function _getStacks(toolbarEl) {
    const id = toolbarEl?.dataset?.toolbarId || '_default';
    if (!_undoStacks[id]) _undoStacks[id] = [];
    if (!_redoStacks[id]) _redoStacks[id] = [];
    return { undo: _undoStacks[id], redo: _redoStacks[id] };
}

function _pushUndo(toolbarEl, text) {
    const { undo, redo } = _getStacks(toolbarEl);
    undo.push(text);
    if (undo.length > 30) undo.shift();
    redo.length = 0; // clear redo on new change
}

function _updateUndoRedoBtns(toolbarEl) {
    const { undo, redo } = _getStacks(toolbarEl);
    const undoBtn = toolbarEl?.querySelector('[data-assist-action="undo"]');
    const redoBtn = toolbarEl?.querySelector('[data-assist-action="redo"]');
    if (undoBtn) undoBtn.disabled = undo.length === 0;
    if (redoBtn) redoBtn.disabled = redo.length === 0;
}

/**
 * Returns HTML for the AI assist toolbar
 */
export function renderAiAssistToolbar({ showFillAi = true, showFillToAll = false, toolbarId = '' } = {}) {
    const tid = toolbarId || 'tb_' + Math.random().toString(36).substr(2, 6);
    return `
        ${showFillAi ? `
        <div class="ai-bar ai-bar--complete" data-testid="ai-bar-complete">
          <button class="ai-btn ai-btn--glow" data-action="fill-ai"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/></svg> Fill with AI</button>
          <button class="ai-btn ai-btn--glow" data-action="regenerate"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg> Regenerate</button>
          ${showFillToAll ? `<button class="ai-btn" data-action="fill-to-all" title="Copy this content to all other platform posts"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Fill to All Posts</button>` : ''}
        </div>
        ` : ''}
        <div class="assist-toolbar" data-testid="ai-assist-toolbar" data-toolbar-id="${tid}">
          <button class="assist-btn assist-btn--primary" data-assist-action="enhance" title="Optimize for engagement"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v5m0 0l3-3m-3 3L9 5"/><circle cx="12" cy="12" r="3"/><path d="M12 19v2m5-9h2M5 12H3m14.5-5.5l1.5-1.5M5.5 18.5L4 20m14.5 0l1.5-1.5M5.5 5.5L4 4"/></svg> AI Enhance</button>
          <button class="assist-btn" data-assist-action="rewrite" data-testid="assist-rewrite" title="Fresh wording"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg> Rewrite</button>
          <button class="assist-btn" data-assist-action="shorten" title="Make shorter"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg> Shorten</button>
          <button class="assist-btn" data-assist-action="expand" title="Add more detail"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Expand</button>
          <button class="assist-btn" data-assist-action="hook" title="Add opening hook"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg> Hook</button>
          <button class="assist-btn" data-assist-action="cta" title="Add call-to-action"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg> CTA</button>
          <button class="assist-btn" data-assist-action="formal" title="Formal tone"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg> Formal</button>
          <button class="assist-btn" data-assist-action="casual" title="Casual tone"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> Casual</button>
          <span class="assist-separator" style="display:inline-block;width:1px;height:20px;background:rgba(255,255,255,0.1);margin:0 4px;vertical-align:middle;"></span>
          <button class="assist-btn assist-btn--undo" data-assist-action="undo" title="Undo last AI change" disabled><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg></button>
          <button class="assist-btn assist-btn--redo" data-assist-action="redo" title="Redo" disabled><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></svg></button>
        </div>
    `;
}

/**
 * Handles AI Assist click events — every button is immediate action
 * @param {Event} e - click event
 * @param {Function} getContent - returns current content text
 * @param {Function} setContent - sets new content text after enhancement
 * @param {string} platform - platform name
 * @param {Function} [onToast] - optional callback for toast messages
 * @returns {boolean} true if the event was handled
 */
export async function handleAiAssistClick(e, getContent, setContent, platform, onToast) {
    // AI Assist — immediate action on any button
    const assistBtn = e.target.closest('[data-assist-action]');
    if (assistBtn) {
        const toolbar = assistBtn.closest('.assist-toolbar');
        const action = assistBtn.dataset.assistAction;

        // Handle undo
        if (action === 'undo') {
            const { undo, redo } = _getStacks(toolbar);
            if (undo.length === 0) return true;
            const current = getContent();
            redo.push(current);
            const prev = undo.pop();
            setContent(prev);
            _updateUndoRedoBtns(toolbar);
            return true;
        }

        // Handle redo
        if (action === 'redo') {
            const { undo, redo } = _getStacks(toolbar);
            if (redo.length === 0) return true;
            const current = getContent();
            undo.push(current);
            const next = redo.pop();
            setContent(next);
            _updateUndoRedoBtns(toolbar);
            return true;
        }

        // All other actions — run immediately
        const content = getContent();
        if (!content?.trim()) {
            if (onToast) onToast('Write some content first, then use AI to enhance it.', 'info');
            return true;
        }

        // Save current state for undo
        _pushUndo(toolbar, content);

        // Disable buttons during processing
        const allBtns = toolbar?.querySelectorAll('.assist-btn') || [];
        allBtns.forEach(b => b.disabled = true);
        const origHTML = assistBtn.innerHTML;
        assistBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg> Working...';

        try {
            const res = await client.enhanceContent({ content, action, platform: platform || 'general' });
            let newText = content;
            if (res.ok && res.data?.text) {
                newText = res.data.text;
            } else if (res.enhanced) {
                newText = res.enhanced;
            }
            // Clean up LLM responses: strip JSON/markdown + decode HTML entities
            newText = _cleanLlmText(newText);
            setContent(newText);
            allBtns.forEach(b => b.disabled = false);
            assistBtn.innerHTML = origHTML;
            _updateUndoRedoBtns(toolbar);
        } catch (err) {
            if (onToast) onToast(err.message || 'AI enhancement failed. Please try again.', 'error');
            // Undo the push since it failed
            const { undo } = _getStacks(toolbar);
            undo.pop();
            allBtns.forEach(b => b.disabled = false);
            assistBtn.innerHTML = origHTML;
            _updateUndoRedoBtns(toolbar);
        }
        return true;
    }

    // Fill with AI
    const fillBtn = e.target.closest('[data-action="fill-ai"]');
    if (fillBtn) {
        const toolbar = fillBtn.closest('.ai-bar')?.parentElement?.querySelector('.assist-toolbar');
        const content = getContent();
        if (content?.trim()) _pushUndo(toolbar, content);

        fillBtn.disabled = true;
        fillBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg> Generating...';
        try {
            const res = await client.enhanceContent({ content: content || '', action: 'fill', platform: platform || 'general' });
            let newText = '';
            if (res.ok && res.data?.text) newText = res.data.text;
            else if (res.enhanced) newText = res.enhanced;
            if (newText) setContent(_cleanLlmText(newText));
            _updateUndoRedoBtns(toolbar);
        } catch (err) {
            if (onToast) onToast(err.message || 'AI generation failed.', 'error');
        }
        fillBtn.disabled = false;
        fillBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/></svg> Fill with AI';
        return true;
    }

    // Regenerate
    const regenBtn = e.target.closest('[data-action="regenerate"]');
    if (regenBtn) {
        const toolbar = regenBtn.closest('.ai-bar')?.parentElement?.querySelector('.assist-toolbar');
        const content = getContent();
        if (content?.trim()) _pushUndo(toolbar, content);

        regenBtn.disabled = true;
        regenBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg> Regenerating...';
        try {
            const res = await client.enhanceContent({ content: content || '', action: 'regenerate', platform: platform || 'general' });
            let newText = '';
            if (res.ok && res.data?.text) newText = res.data.text;
            else if (res.enhanced) newText = res.enhanced;
            if (newText) setContent(_cleanLlmText(newText));
            _updateUndoRedoBtns(toolbar);
        } catch (err) {
            if (onToast) onToast(err.message || 'Regeneration failed.', 'error');
        }
        regenBtn.disabled = false;
        regenBtn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg> Regenerate';
        return true;
    }

    return false;
}
